/**
 * Hook for listening to asset operation results via native WebSocket.
 * Backend returns 201 PROCESSING for submit/approve/reject/version,
 * then delivers the actual result via WS.
 *
 * Connection: wss://<host>/ws/assets?token=<jwt>
 */

import { useEffect, useRef, useState } from 'react'
import useAuthStore from '@/stores/useAuthStore'
import authSdk from '@/utils/sdk'
import {
    AssetOperationEvent,
    UseAssetWebSocketOptions,
} from '../types/websocket'

const MAX_RECONNECT_ATTEMPTS = 5
const MAX_RECONNECT_DELAY_MS = 30_000

/**
 * Derive WebSocket URL from APP_API_URL.
 * e.g. https://api-staging-datacentral.vnetwork.dev/api/v4
 *    → wss://api-staging-datacentral.vnetwork.dev/ws/assets
 */
const getWsUrl = (token: string): string | null => {
    const apiUrl = import.meta.env.APP_API_URL
    if (!apiUrl) return null

    try {
        const url = new URL(apiUrl)
        url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
        url.pathname = '/ws/assets'
        url.search = `token=${token}`
        return url.toString()
    } catch {
        console.error('[AssetWS] Failed to parse API URL:', apiUrl)
        return null
    }
}

export const useAssetWebSocket = (options: UseAssetWebSocketOptions = {}) => {
    const { employee } = useAuthStore()
    const wsRef = useRef<WebSocket | null>(null)
    const [isConnected, setIsConnected] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const reconnectAttemptsRef = useRef(0)
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    )
    const enabledRef = useRef(options.enabled ?? true)
    enabledRef.current = options.enabled ?? true

    // Store callbacks in refs to avoid reconnection on callback change
    const onCompletedRef = useRef(options.onCompleted)
    onCompletedRef.current = options.onCompleted
    const onFailedRef = useRef(options.onFailed)
    onFailedRef.current = options.onFailed

    // Use a ref for connect/reconnect to break circular dependency
    const connectRef = useRef<() => void>(() => {})

    const clearReconnectTimeout = () => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current)
            reconnectTimeoutRef.current = null
        }
    }

    const scheduleReconnect = () => {
        if (!enabledRef.current) return
        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
            console.log('[AssetWS] Max reconnect attempts reached')
            return
        }

        const attempt = reconnectAttemptsRef.current
        const delay = Math.min(1000 * 2 ** attempt, MAX_RECONNECT_DELAY_MS)
        reconnectAttemptsRef.current += 1

        console.log(
            `[AssetWS] Reconnecting in ${delay}ms (${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`
        )

        reconnectTimeoutRef.current = setTimeout(() => {
            connectRef.current()
        }, delay)
    }

    connectRef.current = () => {
        // Don't connect if disabled or already open/connecting
        if (!enabledRef.current) return
        if (
            wsRef.current?.readyState === WebSocket.OPEN ||
            wsRef.current?.readyState === WebSocket.CONNECTING
        )
            return

        const accessToken = authSdk.getAccessToken()
        if (!accessToken) {
            console.error('[AssetWS] No access token')
            setError('No access token')
            return
        }

        const wsUrl = getWsUrl(accessToken)
        if (!wsUrl) {
            console.warn('[AssetWS] WebSocket URL not configured')
            return
        }

        console.log(
            '[AssetWS] Connecting to',
            wsUrl.replace(/token=.*/, 'token=***')
        )

        const ws = new WebSocket(wsUrl)
        wsRef.current = ws

        ws.onopen = () => {
            console.log('[AssetWS] Connected')
            setIsConnected(true)
            setError(null)
            reconnectAttemptsRef.current = 0
        }

        ws.onmessage = (event) => {
            try {
                const parsed = JSON.parse(event.data)
                console.log('[AssetWS] Message received:', parsed)

                // Validate required fields before dispatching
                if (!parsed?.event || !parsed?.data?.operation) {
                    console.warn('[AssetWS] Invalid message format:', parsed)
                    return
                }

                const msg = parsed as AssetOperationEvent

                if (msg.event === 'asset_operation_completed') {
                    onCompletedRef.current?.(msg.data)
                } else if (msg.event === 'asset_operation_failed') {
                    onFailedRef.current?.(msg.data)
                }
            } catch (err) {
                console.error('[AssetWS] Failed to parse message:', err)
            }
        }

        ws.onerror = (event) => {
            console.error('[AssetWS] Error:', event)
            setError('WebSocket error')
        }

        ws.onclose = (event) => {
            console.log(
                '[AssetWS] Disconnected, code:',
                event.code,
                'reason:',
                event.reason
            )
            setIsConnected(false)
            wsRef.current = null
            // Only reconnect if not intentionally closed (code 1000 = normal closure)
            if (event.code !== 1000) {
                scheduleReconnect()
            }
        }
    }

    const disconnect = () => {
        clearReconnectTimeout()
        if (wsRef.current) {
            wsRef.current.close(1000, 'Component unmount')
            wsRef.current = null
        }
        setIsConnected(false)
    }

    // Single effect: connect on mount, disconnect on unmount
    useEffect(() => {
        if (employee) {
            connectRef.current()
        }
        return () => {
            disconnect()
        }
        // Only depend on employee identity, not callbacks
    }, [employee?.employee_id])

    return {
        isConnected,
        error,
        connect: () => connectRef.current(),
        disconnect,
    }
}

export default useAssetWebSocket
